import { TabGroup, TabPanel, TabPanels } from '@headlessui/react'
import Alert from 'components/base/Alert'
import Badge from 'components/base/Badge'
import Button from 'components/base/Button'
import Card from 'components/base/Card'
import TabList from 'components/base/TabList'
import Container from 'components/landing/Container'
import BannersContainer from 'components/ads/BannersContainer'
import content from 'components/landing/mcp/content.json'
import Header from 'components/landing/Header'
import { FC, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import Head from 'next/head'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

type Kind = 'analytics' | 'inner-analytics'
type Host = 'sellematics' | 'sellmonitor' | 'mayak'

const CopyButton: FC<{ text: string; label?: string }, 'button'> = ({
  text,
  label = 'Скопировать',
  className,
}) => {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timeout.current !== null) clearTimeout(timeout.current)
  }, [])

  return (
    <Button
      theme="secondary"
      className={c`min-h-10 justify-center ${className}`}
      onClick={async () => {
        if (timeout.current !== null) clearTimeout(timeout.current)
        try {
          try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable')
            await navigator.clipboard.writeText(text)
          } catch {
            const textarea = document.createElement('textarea')
            const focusedElement = document.activeElement
            textarea.value = text
            textarea.style.position = 'fixed'
            textarea.style.left = '-9999px'
            document.body.appendChild(textarea)
            textarea.select()
            const copied = document.execCommand('copy')
            textarea.remove()
            if (focusedElement instanceof HTMLElement) focusedElement.focus()
            if (!copied) throw new Error('Copy failed')
          }
          setStatus('success')
        } catch {
          setStatus('error')
        }
        timeout.current = setTimeout(() => setStatus('idle'), 1600)
      }}
    >
      <span aria-live="polite">
        {status === 'success' ? 'Скопировано' : status === 'error' ? 'Не удалось скопировать' : label}
      </span>
    </Button>
  )
}

const CodeBlock: FC<{ text: string; label?: string }> = ({ text, label, className }) => (
  <div className={c`mt-3 grid rounded-lg border border-gray-200 bg-gray-50 min-[561px]:grid-cols-[minmax(0,1fr)_auto] ${className}`}>
    <pre className="m-0 min-h-[58px] min-w-0 whitespace-pre-wrap p-4 font-mono text-sm leading-[1.55] text-gray-900 [overflow-wrap:anywhere]">{text}</pre>
    <CopyButton text={text} label={label} className="m-[9px] self-start min-[561px]:whitespace-nowrap" />
  </div>
)

const Section: FC<{ title: string }, 'section'> = ({ title, children }) => (
  <Container className="!py-10 sm:!py-14 even:bg-white">
    <h2 className="mb-6 text-2xl font-medium tracking-tight text-gray-900 sm:text-3xl">{title}</h2>
    {children}
  </Container>
)

const Cards: FC<{ items: { title: string; text: string }[]; numbered?: boolean }> = ({ items, numbered = false }) => (
  <div className={c`grid grid-cols-1 gap-6 ${numbered} min-[881px]:grid-cols-3 | min-[881px]:grid-cols-2`}>
    {items.map((item, index) => (
      <Card key={item.title} className="p-6">
        {numbered && (
          <span className="mb-3.5 inline-grid h-8 w-8 place-items-center rounded-lg bg-primary-50 font-semibold text-primary-700">{index + 1}</span>
        )}
        <h3 className="mb-2.5 text-lg font-semibold leading-[1.35]">{item.title}</h3>
        <p className="text-gray-600">{item.text}</p>
      </Card>
    ))}
  </div>
)

const McpInstructions: FC<{ kind: Kind }> = ({ kind }) => {
  const ctx = useUserContext()
  const host = ctx.theme.hostType as Host
  const page = content[`${host}-${kind}`]

  return (
    <>
      <Head>
        <title>{page.title}</title>
        <meta name="robots" content="index, follow" />
        <meta name="description" content={page.description} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={ctx.theme.title} />
        <meta property="og:title" content={page.ogTitle} />
        <meta property="og:description" content={page.ogDescription} />
        <meta property="og:url" content={`${ctx.theme.canonicalURL}/${kind === 'analytics' ? 'marketplace' : 'store'}-analytics-mcp/`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={page.twitterTitle} />
        <meta name="twitter:description" content={page.twitterDescription} />
        <link rel="canonical" href={`${ctx.theme.canonicalURL}/${kind === 'analytics' ? 'marketplace' : 'store'}-analytics-mcp/`} />
      </Head>
      <BannersContainer scope="landing" placement={`mcp-${kind}`} />
      <Header />
      <div className="bg-gray-50 text-base leading-[1.6] text-gray-900 [overflow-wrap:anywhere] [&_code]:rounded-md [&_code]:border [&_code]:border-gray-200 [&_code]:bg-gray-50 [&_code]:px-[5px] [&_code]:py-px [&_code]:font-mono [&_code]:text-[0.92em] [&_code]:text-gray-900 [&_strong]:font-semibold">
        <nav className="border-b border-gray-200 bg-white" aria-label="Выбор MCP-сервера">
          <div className="mx-auto grid max-w-7xl px-4 sm:px-6 lg:px-8 grid-cols-1 gap-2.5 py-3.5 min-[561px]:grid-cols-2">
            {(['analytics', 'inner-analytics'] as const).map((item) => {
              const label = item === 'analytics' ? 'Внешняя аналитика' : 'Внутренняя аналитика'
              const detail = item === 'analytics'
                ? 'Рынок, конкуренты, ниши и тренды'
                : 'Ваши магазины, продажи, расходы и остатки'
              return (
                <Link
                  key={item}
                  href={`/${item === 'analytics' ? 'marketplace' : 'store'}-analytics-mcp/`}
                  aria-current={item === kind ? 'page' : undefined}
                  className={c`grid gap-px rounded-lg border px-3.5 py-2.5 leading-[1.35] hover:border-primary-500 ${item === kind} border-primary-500 bg-primary-50 text-primary-700 | border-gray-200 bg-gray-50 text-gray-900`}
                >
                  <span className="font-semibold">{label}</span>
                  <small className="text-xs text-gray-500">{detail}</small>
                </Link>
              )
            })}
          </div>
        </nav>
        <section className="bg-white">
          <div className="mx-auto grid max-w-7xl px-4 sm:px-6 lg:px-8 grid-cols-1 items-center gap-10 py-12 lg:grid-cols-[minmax(0,1fr)_360px] lg:py-20">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-2.5">
                <Badge className="border border-primary-300 bg-primary-50 !py-1 !font-semibold text-primary-700">{page.hero.label}</Badge>
                <Badge className="border border-primary-300 bg-primary-50 !py-1 !font-semibold text-primary-700">Beta</Badge>
              </div>
              <div className="mb-[18px] flex max-w-[720px] flex-wrap gap-2" aria-label="Ключевые темы MCP">
                {page.hero.tags.map((tag) => (
                  <Badge key={tag} small className="!rounded-md border border-gray-200 bg-white !py-1 text-gray-600">{tag}</Badge>
                ))}
              </div>
              <h1 className="mb-[18px] max-w-[760px] text-3xl sm:text-4xl xl:text-5xl tracking-tight !leading-[1.05] [font-variation-settings:'wdth'_150,'wght'_650]">
                {page.hero.title.split(/(AI-\S+)/).map((part, index) => (
                  part.startsWith('AI-') ? <span key={index} className="whitespace-nowrap">{part}</span> : part
                ))}
              </h1>
              <p className="mb-[26px] max-w-[720px] text-lg text-gray-500">{page.hero.lead}</p>
              <p className="mt-3.5 max-w-[720px] text-sm text-gray-500">{page.hero.betaNote}</p>
              <div className="mt-4 flex max-w-[720px] flex-col items-stretch justify-between gap-6 rounded-lg border border-primary-200 bg-primary-50 p-[18px] min-[561px]:flex-row min-[561px]:items-center">
                <div>
                  <p className="mb-1 font-semibold text-primary-700">{page.hero.promoTitle}</p>
                  <p className="text-sm text-gray-500">
                    {page.hero.promoText}{page.hero.promoCode && <code>{page.hero.promoCode}</code>}{page.hero.promoSuffix}
                  </p>
                </div>
                <Button as={Link} href={page.hero.signupUrl} theme="primary" className="justify-center whitespace-nowrap !px-6 !py-3">Зарегистрироваться</Button>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-[18px] min-[881px]:p-6" aria-hidden="true">
              <div className="grid gap-3.5">
                {page.hero.flow.map((item, index) => (
                  <div key={item.title} className="grid min-h-[62px] grid-cols-[42px_minmax(0,1fr)] items-center gap-3 rounded-lg border border-gray-200 bg-white p-2.5">
                    <div className="grid h-[42px] w-[42px] place-items-center rounded-lg bg-primary-50 font-semibold text-primary-600">{index + 1}</div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-gray-500">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <Section title="Что нужно перед подключением">
          <Cards items={page.prerequisites} numbered />
        </Section>
        <Section title="Адрес MCP сервера">
          <div className="grid max-w-[860px] gap-3.5 rounded-lg border border-gray-200 bg-white p-5">
            <p className="mb-4 text-gray-500">{page.serverDescription}</p>
            <div className="mt-4 grid gap-2.5">
              <div className="flex flex-col items-stretch justify-between gap-3 min-[561px]:flex-row min-[561px]:items-center">
                <p className="text-sm font-semibold text-gray-500">URL для подключения</p>
                <CopyButton text={page.serverUrl} />
              </div>
              <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-900">{page.serverUrl}</pre>
            </div>
          </div>
        </Section>
        <Section title="Быстрое подключение">
          <Cards items={page.quickSteps} numbered />
        </Section>
        <Section title="Добавление в программу">
          <TabGroup key={`${host}-${kind}`} className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-col items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4 min-[561px]:flex-row min-[561px]:items-center">
              <span className="font-semibold">Выберите программу и выполните шаги</span>
              <Badge small className="whitespace-nowrap bg-primary-50 text-primary-700">Remote MCP · OAuth</Badge>
            </div>
            <div className="overflow-x-auto border-b border-gray-200 p-4">
              <TabList tabs={['ChatGPT (Codex)', 'Cursor', 'VS Code', 'Claude Code']} />
            </div>
            <TabPanels className="[&>div]:p-[18px] min-[561px]:[&>div]:p-6 [&_h3]:mb-2.5 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-[1.35] [&_ol]:grid [&_ol]:list-decimal [&_ol]:gap-2.5 [&_ol]:pl-[22px]">
              <TabPanel>
                <h3>Приложение ChatGPT — режим Codex</h3>
                <ol>
                  <li>Переключитесь в режим Codex. В верхней части приложения ChatGPT откройте меню выбора режима и выберите Codex. В обычном режиме ChatGPT этот MCP использоваться не будет.</li>
                  <li>Откройте Настройки → Плагины → MCP и нажмите Добавить → Добавить сервер MCP.</li>
                  <li>Выберите транспорт Streamable HTTP, укажите имя <code>{page.serverName}</code> и вставьте в поле URL адрес из блока ниже. Поля Bearer token и Headers оставьте пустыми.</li>
                </ol>
                <CodeBlock text={page.serverUrl} label="Скопировать URL" />
                <ol start={4} className="mt-[18px]">
                  <li>Сохраните сервер и нажмите Перезапустить.</li>
                  <li>В списке MCP-серверов нажмите Авторизовать и подтвердите подключение к аккаунту {ctx.theme.title} в браузере.</li>
                  <li>Вернитесь в чат в режиме Codex. Проверить подключённый сервер можно командой <code>/mcp</code>.</li>
                </ol>
                <Alert type="warning" className="mt-5">
                  Если кнопка Авторизовать не срабатывает, перед её нажатием временно отключите VPN. Нажмите Авторизовать, завершите вход в браузере, после чего VPN можно включить снова.
                </Alert>
                <div className="mt-5 border-t border-gray-200 pt-4">
                  <details>
                    <summary className="cursor-pointer font-semibold">Альтернатива: Codex CLI или config.toml</summary>
                    <p className="mt-4">Добавьте remote MCP сервер и запустите OAuth-вход из терминала:</p>
                    <CodeBlock className="[&>button]:self-center" text={`codex mcp add ${page.serverName} --url ${page.serverUrl}\ncodex mcp login ${page.serverName}`} />
                    <p className="mt-6">Либо добавьте сервер вручную в <code>~/.codex/config.toml</code>:</p>
                    <CodeBlock className="[&>button]:self-center" text={`[mcp_servers.${page.serverName}]\nurl = "${page.serverUrl}"`} />
                  </details>
                </div>
                <p className="mt-4 text-sm"><a href="https://developers.openai.com/codex/mcp/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Документация Codex MCP</a></p>
              </TabPanel>
              <TabPanel>
                <h3>Cursor</h3>
                <ol>
                  <li>Откройте Cursor Settings и перейдите в раздел MCP или Tools &amp; Integrations.</li>
                  <li>Добавьте новый MCP server и вставьте конфигурацию:
                    <CodeBlock text={JSON.stringify({ mcpServers: { [page.serverName]: { url: page.serverUrl } } }, null, 2)} />
                  </li>
                  <li>Сохраните конфиг и нажмите Connect или Login. Cursor откроет OAuth-страницу {ctx.theme.title}.</li>
                </ol>
              </TabPanel>
              <TabPanel>
                <h3>VS Code</h3>
                <ol>
                  <li>Откройте Command Palette и выберите <code>MCP: Add Server</code>.</li>
                  <li>Выберите HTTP / Server-Sent Events и вставьте адрес <code>{page.serverUrl}</code>.</li>
                  <li>Укажите имя <code>{page.serverName}</code>, затем откройте <code>MCP: List Servers</code> и запустите сервер.</li>
                  <li>Завершите OAuth-авторизацию в браузере.</li>
                </ol>
                <CodeBlock text={page.serverUrl} label="Скопировать URL" />
              </TabPanel>
              <TabPanel>
                <h3>Claude Code</h3>
                <ol>
                  <li>Откройте терминал и добавьте HTTP MCP сервер:
                    <CodeBlock text={`claude mcp add --transport http --scope user ${page.serverName} ${page.serverUrl}`} />
                  </li>
                  <li>Откройте Claude Code. OAuth-вход должен запуститься автоматически.</li>
                  <li>Если вход не открылся, выполните <code>/mcp</code> внутри Claude Code и выберите авторизацию сервера.</li>
                </ol>
              </TabPanel>
            </TabPanels>
          </TabGroup>
          <Alert type="warning" className="mt-6" text={page.securityNote} />
        </Section>
        <Section title="Что можно спрашивать">
          <p className="mb-4 text-gray-500">{page.examplesIntro}</p>
          <Cards items={page.examples} />
          {'exampleGroups' in page && page.exampleGroups.map((group) => (
            <div key={group.title} className="mt-8">
              <h3 className="mb-4 text-xl font-semibold text-gray-900">{group.title}</h3>
              <Cards items={group.examples} />
            </div>
          ))}
          <div className="mt-[18px] flex flex-wrap gap-2.5" aria-label={kind === 'analytics' ? 'Возможности MCP' : 'Возможности MCP внутренней аналитики'}>
            {page.tools.map((tool) => (
              <Badge key={tool} className="!rounded-md border border-gray-200 bg-white !py-1 text-gray-700">{tool}</Badge>
            ))}
          </div>
        </Section>
        <Section title="Если не подключается">
          <Cards items={page.help} />
        </Section>
        <Section title="Технические адреса">
          {page.technicalUrls.map((item) => (
            <details key={item.title} className="border-t border-gray-200 py-[18px] last:border-b">
              <summary className="cursor-pointer font-semibold">{item.title}</summary>
              <div className="mt-4 grid gap-2.5">
                <div className="flex flex-col items-stretch justify-between gap-3 min-[561px]:flex-row min-[561px]:items-center">
                  <p className="text-sm font-semibold text-gray-500">URL</p>
                  <CopyButton text={item.url} />
                </div>
                <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-900">{item.url}</pre>
              </div>
            </details>
          ))}
        </Section>
        <div className="pb-10 pt-7 text-sm text-gray-500">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{page.footer}</div>
        </div>
      </div>
    </>
  )
}

export default McpInstructions
